package components

import androidx.compose.runtime.*
import api.TradeNewsApiClient
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.isActive
import models.NewsItem
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.*

@Composable
fun NewsList(
    selectedCategory: String?,
    client: TradeNewsApiClient,
    onFetchStateChanged: (Throwable?) -> Unit = {}
) {
    var newsItems by remember { mutableStateOf<List<NewsItem>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var retryNonce by remember { mutableStateOf(0) }

    LaunchedEffect(selectedCategory, retryNonce, client) {
        isLoading = true
        error = null
        onFetchStateChanged(null)
        try {
            newsItems = client.fetchNewsItems(selectedCategory)
            error = null
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            newsItems = emptyList()
            error = buildNewsFetchErrorMessage(e)
            onFetchStateChanged(e)
            console.error("Failed to fetch news", e)
        } finally {
            if (currentCoroutineContext().isActive) {
                isLoading = false
            }
        }
    }

    Div(attrs = {
        classes("news-list")
    }) {
        when {
            isLoading -> {
                Div(attrs = {
                    classes("loading-container")
                }) {
                    Div(attrs = {
                        classes("loading-spinner")
                    })
                    P { Text("ニュースを読み込んでいます...") }
                }
            }
            error != null -> {
                Div(attrs = {
                    classes("error-container")
                }) {
                    H3(attrs = {
                        classes("error-title")
                    }) {
                        Text("ニュースを取得できなかった")
                    }
                    P(attrs = {
                        classes("error-message")
                    }) {
                        Text(error!!)
                    }
                    P(attrs = {
                        classes("error-hint")
                    }) {
                        Text(buildNewsFetchRecoveryHint(error!!))
                    }
                    Button(attrs = {
                        classes("retry-button")
                        onClick { retryNonce += 1 }
                    }) {
                        Text("再試行する")
                    }
                }
            }
            newsItems.isEmpty() -> {
                Div(attrs = {
                    classes("empty-container")
                }) {
                    P { Text("ニュースが見つかりませんでした。") }
                }
            }
            else -> {
                Div(attrs = {
                    classes("news-grid")
                }) {
                    newsItems.forEach { newsItem ->
                        NewsCard(newsItem)
                    }
                }
            }
        }
    }
}

fun buildNewsFetchErrorMessage(error: Throwable): String {
    val message = error.message.orEmpty()

    return when {
        message.contains("Backend unavailable", ignoreCase = true) ->
            "バックエンドが一時的に応答していない。しばらくしてから再試行してくれ。"
        message.contains("timeout", ignoreCase = true) ->
            "通信がタイムアウトした。ネットワーク状態を確認して再試行してくれ。"
        message.contains("Failed to fetch", ignoreCase = true) ->
            "API に接続できなかった。バックエンドの起動状態を確認してくれ。"
        else -> "ニュースの取得に失敗した。時間をおいて再試行してくれ。"
    }
}

fun buildNewsFetchRecoveryHint(message: String): String {
    return when {
        message.contains("バックエンド", ignoreCase = true) ->
            "ローカル開発中なら backend を起動してから、もう一回試してくれ。"
        message.contains("タイムアウト", ignoreCase = true) ->
            "回線が不安定な時に出やすい。少し待ってから再試行が有効だ。"
        else ->
            "一時的な障害の可能性がある。再試行で復旧しない時はログを確認してくれ。"
    }
}

object NewsListStyles : StyleSheet() {
    init {
        ".news-list" style {
            marginTop(2.em)
        }

        ".news-grid" style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(3, 1fr)")
            gap(1.25.em)
            marginTop(1.em)
        }

        // Bento Grid: 先頭カードは2列幅で目立たせる
        ".news-grid > *:first-child" style {
            property("grid-column", "span 2")
        }

        // レスポンシブデザイン - タブレット
        media("(max-width: 1024px)") {
            ".news-grid" style {
                property("grid-template-columns", "repeat(2, 1fr)")
            }
        }

        // レスポンシブデザイン - モバイル
        media("(max-width: 600px)") {
            ".news-grid" style {
                property("grid-template-columns", "1fr")
            }
            ".news-grid > *:first-child" style {
                property("grid-column", "span 1")
            }
        }

        ".loading-container" style {
            textAlign("center")
            padding(3.em)
            color(Color("#94a3b8"))
        }

        ".loading-spinner" style {
            property("border", "4px solid rgba(255,255,255,0.1)")
            property("border-top", "4px solid #3b82f6")
            borderRadius(50.percent)
            width(40.px)
            height(40.px)
            property("animation", "spin 1s linear infinite")
            property("margin", "0 auto 1em")
        }

        ".error-container" style {
            backgroundColor(rgba(127, 29, 29, 0.2))
            property("border", "1px solid rgba(252, 165, 165, 0.3)")
            borderRadius(20.px)
            padding(1.5.em)
            margin(1.em)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(0.75.em)
            property("backdrop-filter", "blur(16px)")
        }

        ".error-title" style {
            color(Color("#fca5a5"))
            margin(0.px)
            fontSize(1.15.em)
        }

        ".error-message" style {
            color(Color("#fca5a5"))
            margin(0.px)
            opacity(0.8)
        }

        ".error-hint" style {
            color(Color("#94a3b8"))
            margin(0.px)
            fontSize(0.95.em)
        }

        ".retry-button" style {
            alignSelf("flex-start")
            property("background", "linear-gradient(135deg, #ef4444, #dc2626)")
            color(Color.white)
            property("border", "none")
            borderRadius(999.px)
            padding(0.65.em, 1.1.em)
            fontWeight(600)
            cursor("pointer")
            fontFamily("inherit")
        }

        ".retry-button:hover" style {
            property("background", "linear-gradient(135deg, #dc2626, #b91c1c)")
        }

        ".empty-container" style {
            textAlign("center")
            padding(3.em)
            color(Color("#64748b"))
        }

        media("(prefers-reduced-motion: no-preference)") {
            // Animation defined in CSS
        }
    }
}
